/**
 * Ask, the evaluator: runs a parsed query over the live entities. Pure (time comes in as
 * `now`), so every answer is unit tested and the same predicate filters the list pages.
 */
import type { AuditEntry, Checkout, Machine, ModelPrice, Pipeline, PricingTable, Project, Session } from '@orgus/protocol';
import { ACTION_LABEL, machineLiveness, sessionStateOf } from '@orgus/protocol';
import { modelLabel } from '../sessions/labels.ts';
import type { AskEntity, AskQuery, Clause, CompareOp, GroupField, SortField, TimeWindow } from './parse.ts';
import { whereText } from './parse.ts';

export interface AskData {
  sessions: Record<string, Session>;
  machines: Record<string, Machine>;
  projects: Record<string, Project>;
  checkouts: Record<string, Checkout>;
  pipelines: Record<string, Pipeline>;
  audit: AuditEntry[];
  /** USD per session when the costs module reports it; otherwise estimated from tokens. */
  sessionCost?: Record<string, number>;
  /** The hub's price list (state.ext.costs.pricing), for sessions the costs module has no figure for. */
  pricing?: PricingTable;
  now: number;
}

const H = 3_600_000;
const D = 24 * H;

/** [from, to) in ms for a time window, in local time. */
export function windowRange(w: TimeWindow, now: number): [number, number] {
  const d = new Date(now);
  const midnight = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  // Weeks start on Monday.
  const dow = (d.getDay() + 6) % 7;
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dow).getTime();
  switch (w.kind) {
    case 'today':
      return [midnight, Infinity];
    case 'yesterday':
      return [new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1).getTime(), midnight];
    case 'week':
      return [monday, Infinity];
    case 'lastweek':
      return [new Date(d.getFullYear(), d.getMonth(), d.getDate() - dow - 7).getTime(), monday];
    case 'month':
      return [new Date(d.getFullYear(), d.getMonth(), 1).getTime(), Infinity];
    case 'last': {
      const unit = w.unit === 'minute' ? 60_000 : w.unit === 'hour' ? H : w.unit === 'day' ? D : 7 * D;
      return [now - w.n * unit, Infinity];
    }
  }
}

function cmp(a: number, op: CompareOp, b: number): boolean {
  switch (op) {
    case '>':
      return a > b;
    case '<':
      return a < b;
    case '>=':
      return a >= b;
    case '<=':
      return a <= b;
    case '=':
      return a === b;
  }
}

// ---------------------------------------------------------------------------
// Cost estimate (used only when the costs module has no figure for a session)
// ---------------------------------------------------------------------------

type Rates = Pick<ModelPrice, 'match' | 'input' | 'output' | 'cacheRead'>;
const r = (match: string, input: number, output: number, cacheRead: number): Rates => ({ match, input, output, cacheRead });

/**
 * List prices, USD per million tokens, used only when the hub sends no price list (the costs
 * module is off, or demo mode). The same figures as the costs module's table of 2026-10-16
 * (apps/hub/src/ext/costs/pricing.ts); the hub's own table wins whenever it is present.
 */
export const FALLBACK_PRICES: readonly Rates[] = [
  r('claude-fable-5-1', 10, 50, 0.25),
  r('claude-mythos-5-1', 10, 50, 0.25),
  r('claude-fable-5', 10, 50, 1),
  r('claude-mythos-5', 10, 50, 1),
  r('claude-opus-5-5', 4, 20, 0.2),
  r('claude-opus-5', 5, 25, 0.5),
  r('claude-opus-4-8', 5, 25, 0.5),
  r('claude-opus-4-7', 5, 25, 0.5),
  r('claude-opus-4-6', 5, 25, 0.5),
  r('claude-opus-4-5', 5, 25, 0.5),
  r('claude-opus-4-1', 15, 75, 1.5),
  r('claude-opus-4', 15, 75, 1.5),
  r('claude-sonnet-5-5', 2, 10, 0.1),
  r('claude-sonnet-5', 2, 10, 0.2),
  r('claude-sonnet-4-6', 3, 15, 0.3),
  r('claude-sonnet-4-5', 3, 15, 0.3),
  r('claude-sonnet-4', 3, 15, 0.3),
  r('claude-haiku-5-5', 0.1, 0.5, 0.01),
  r('claude-haiku-4-5', 1, 5, 0.1),
  r('claude-3-5-haiku', 0.8, 4, 0.08),
  r('claude-haiku-3-5', 0.8, 4, 0.08),
];

/** A model the list does not know is priced as the newest of its family (or Sonnet). */
const FAMILY_DEFAULT: [RegExp, string][] = [
  [/fable/, 'claude-fable-5-1'],
  [/mythos/, 'claude-mythos-5-1'],
  [/opus/, 'claude-opus-5-5'],
  [/haiku/, 'claude-haiku-5-5'],
  [/sonnet/, 'claude-sonnet-5-5'],
];

/** 'us.anthropic.claude-sonnet-4-5-20250929-v1:0' and 'claude-opus-4-5@20251101' become 'claude-...' ids (as the hub does). */
export function normalizeModel(model: string): string {
  const m = model.toLowerCase();
  const i = m.indexOf('claude-');
  return i >= 0 ? m.slice(i) : m;
}

/** Longest matching prefix that ends on a word boundary, as the hub's findPrice. */
function findRates(id: string, prices: readonly Rates[]): Rates | null {
  let best: Rates | null = null;
  for (const p of prices) {
    if (!id.startsWith(p.match)) continue;
    const next = id.charAt(p.match.length);
    if (next && /[0-9a-z]/.test(next)) continue;
    if (!best || p.match.length > best.match.length) best = p;
  }
  return best;
}

/** The rates a session's model is estimated at. */
export function ratesForModel(model: string | null | undefined, prices: readonly Rates[] = FALLBACK_PRICES): Rates {
  const id = normalizeModel(model ?? '');
  const hit = findRates(id, prices) ?? findRates(id, FALLBACK_PRICES);
  if (hit) return hit;
  const fam = FAMILY_DEFAULT.find(([re]) => re.test(id))?.[1] ?? 'claude-sonnet-5-5';
  return findRates(fam, prices) ?? findRates(fam, FALLBACK_PRICES)!;
}

/** A rough estimate from a session's token counts at list prices (cache writes are not in the counts). */
export function estimateCost(s: Pick<Session, 'model' | 'tokens'>, prices?: readonly Rates[]): number {
  const p = ratesForModel(s.model, prices);
  return (s.tokens.input * p.input + s.tokens.output * p.output + s.tokens.cacheRead * p.cacheRead) / 1e6;
}

export function sessionCostOf(s: Session, d: Pick<AskData, 'sessionCost' | 'pricing'>): number {
  return d.sessionCost?.[s.id] ?? estimateCost(s, d.pricing?.prices);
}

// ---------------------------------------------------------------------------
// Predicates per entity
// ---------------------------------------------------------------------------

const lc = (s: string | null | undefined) => (s ?? '').toLowerCase();

function sessionFailed(s: Session): boolean {
  return sessionStateOf(s) === 'failed' || s.subagents.some((a) => a.status === 'failed') || s.workflows.some((w) => w.status === 'failed');
}

function sessionStatus(s: Session, v: string): boolean {
  if (v === 'live') return s.status !== 'ended';
  if (v === 'failed') return sessionFailed(s);
  return s.status === v;
}

function newestVersion(machines: Record<string, Machine>): string {
  return Object.values(machines)
    .map((m) => m.agentVersion)
    .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))[0] ?? '';
}

/** Searchable text of a session, cached per session object (sessions are replaced, never mutated). */
const hayCache = new WeakMap<Session, string>();
function sessionHay(s: Session, d: AskData): string {
  let h = hayCache.get(s);
  if (h === undefined) {
    h = [s.title, s.lastText, s.cwd, s.model ?? '', d.projects[s.projectId ?? '']?.name ?? '', d.machines[s.machineId]?.name ?? ''].join('\n').toLowerCase();
    hayCache.set(s, h);
  }
  return h;
}

/** Compile one clause once (time windows, lowercase names), then test many sessions fast. */
function compileSessionClause(c: Clause, d: AskData): (s: Session) => boolean {
  if (c.kind === 'time') {
    const [from, to] = windowRange(c.window, d.now);
    return c.field === 'started' ? (s) => s.startedAt >= from && s.startedAt < to : (s) => s.lastActivity >= from && s.lastActivity < to;
  }
  if (c.kind === 'machine') {
    const want = lc(c.name);
    const ids = new Set(Object.values(d.machines).filter((m) => lc(m.name) === want).map((m) => m.id));
    return (s) => ids.has(s.machineId);
  }
  if (c.kind === 'project') {
    if (c.name === 'none') return (s) => s.projectId == null;
    const want = lc(c.name);
    const ids = new Set(Object.values(d.projects).filter((p) => lc(p.name) === want).map((p) => p.id));
    return (s) => s.projectId != null && ids.has(s.projectId);
  }
  if (c.kind === 'text') {
    const words = c.value.split(/\s+/).filter(Boolean);
    return (s) => {
      const hay = sessionHay(s, d);
      return words.every((w) => hay.includes(w));
    };
  }
  return (s) => sessionMatches(s, c, d);
}

function sessionMatches(s: Session, c: Clause, d: AskData): boolean {
  switch (c.kind) {
    case 'status':
      return sessionStatus(s, c.value);
    case 'time': {
      const [from, to] = windowRange(c.window, d.now);
      const t = c.field === 'started' ? s.startedAt : s.lastActivity;
      return t >= from && t < to;
    }
    case 'machine':
      return lc(d.machines[s.machineId]?.name) === lc(c.name);
    case 'project':
      return c.name === 'none' ? s.projectId == null : lc(d.projects[s.projectId ?? '']?.name) === lc(c.name);
    case 'model':
      return lc(s.model).includes(lc(c.value)) || lc(modelLabel(s.model)).includes(lc(c.value));
    case 'text': {
      const hay = [s.title, s.lastText, s.cwd, s.model ?? '', d.projects[s.projectId ?? '']?.name ?? '', d.machines[s.machineId]?.name ?? '']
        .join('\n')
        .toLowerCase();
      return c.value.split(/\s+/).every((w) => hay.includes(w));
    }
    case 'compare': {
      const v =
        c.field === 'tokens'
          ? s.tokens.input + s.tokens.output
          : c.field === 'turns'
            ? s.turns
            : c.field === 'cost'
              ? sessionCostOf(s, d)
              : c.field === 'subagents'
                ? s.subagents.length
                : null;
      return v != null && cmp(v, c.op, c.value);
    }
    case 'has':
      return c.value === 'subagents' ? s.subagents.length > 0 : c.value === 'workflows' ? s.workflows.length > 0 : true;
    case 'origin':
      return s.origin === 'orgus';
    case 'actor':
    case 'action':
      return true;
  }
}

function machineMatches(m: Machine, c: Clause, d: AskData, ctx: { latest: string; liveByMachine: Map<string, number> }): boolean {
  switch (c.kind) {
    case 'status': {
      const l = machineLiveness(m, d.now);
      if (c.value === 'online' || c.value === 'offline' || c.value === 'stale') return l === c.value;
      if (c.value === 'outdated') return !!ctx.latest && m.agentVersion !== ctx.latest;
      if (c.value === 'busy') return l === 'online' && m.load >= 0.7;
      if (c.value === 'idle') return l === 'online' && !ctx.liveByMachine.get(m.id);
      return false;
    }
    case 'time': {
      const [from, to] = windowRange(c.window, d.now);
      return m.lastSeen >= from && m.lastSeen < to;
    }
    case 'machine':
      return lc(m.name) === lc(c.name);
    case 'project':
      return Object.values(d.checkouts).some((x) => x.machineId === m.id && lc(d.projects[x.projectId]?.name) === lc(c.name));
    case 'text':
      return c.value.split(/\s+/).every((w) => `${m.name} ${m.hostname} ${m.os} ${m.labels.join(' ')}`.toLowerCase().includes(w));
    case 'compare': {
      const v = c.field === 'load' ? m.load : c.field === 'memory' ? (m.memTotal ? 1 - m.memFree / m.memTotal : 0) : null;
      return v != null && cmp(v, c.op, c.value);
    }
    default:
      return true;
  }
}

function auditMatches(e: AuditEntry, c: Clause, d: AskData): boolean {
  switch (c.kind) {
    case 'status':
      return e.result === c.value;
    case 'time': {
      const [from, to] = windowRange(c.window, d.now);
      return e.at >= from && e.at < to;
    }
    case 'machine':
      return lc(d.machines[e.machineId]?.name) === lc(c.name);
    case 'actor':
      // actor:login (a person's "Their actions") is that person only; "actor bob" is a search.
      return c.exact ? lc(e.actor) === lc(c.value) : lc(e.actor).includes(lc(c.value));
    case 'action':
      return e.action === c.value;
    case 'text':
      return c.value.split(/\s+/).every((w) => `${e.target} ${e.message ?? ''} ${ACTION_LABEL[e.action]} ${e.actor}`.toLowerCase().includes(w));
    case 'project': {
      const p = Object.values(d.projects).find((x) => lc(x.name) === lc(c.name));
      return !!p && (e.params['projectId'] === p.id || lc(e.target).includes(lc(c.name)));
    }
    default:
      return true;
  }
}

function projectMatches(p: Project, c: Clause, d: AskData, live: Map<string, number>): boolean {
  switch (c.kind) {
    case 'status':
      if (c.value === 'active') return (live.get(p.id) ?? 0) > 0;
      if (c.value === 'idle') return !live.get(p.id);
      if (c.value === 'dirty') return Object.values(d.checkouts).some((x) => x.projectId === p.id && x.dirty > 0);
      return false;
    case 'machine':
      return Object.values(d.checkouts).some((x) => x.projectId === p.id && lc(d.machines[x.machineId]?.name) === lc(c.name));
    case 'project':
      return lc(p.name) === lc(c.name);
    case 'text':
      return c.value.split(/\s+/).every((w) => `${p.name} ${p.remoteUrl ?? ''}`.toLowerCase().includes(w));
    default:
      return true;
  }
}

function checkoutMatches(x: Checkout, c: Clause, d: AskData): boolean {
  switch (c.kind) {
    case 'status':
      if (c.value === 'dirty') return x.dirty > 0;
      if (c.value === 'clean') return x.dirty === 0;
      if (c.value === 'behind') return x.behind > 0;
      if (c.value === 'ahead') return x.ahead > 0;
      if (c.value === 'stale') return d.now - x.updatedAt > 7 * D;
      return false;
    case 'machine':
      return lc(d.machines[x.machineId]?.name) === lc(c.name);
    case 'project':
      return lc(d.projects[x.projectId]?.name) === lc(c.name);
    case 'has':
      return c.value === 'changes' ? x.dirty > 0 : true;
    case 'compare': {
      const v = c.field === 'behind' ? x.behind : c.field === 'changes' ? x.dirty : null;
      return v != null && cmp(v, c.op, c.value);
    }
    case 'text':
      return c.value.split(/\s+/).every((w) => `${x.path} ${x.branch ?? ''} ${x.headSubject}`.toLowerCase().includes(w));
    case 'time': {
      const [from, to] = windowRange(c.window, d.now);
      return x.updatedAt >= from && x.updatedAt < to;
    }
    default:
      return true;
  }
}

function pipelineMatches(p: Pipeline, c: Clause, d: AskData): boolean {
  switch (c.kind) {
    case 'status':
      return p.state === c.value;
    case 'machine':
      return lc(d.machines[p.machineId]?.name) === lc(c.name);
    case 'project':
      return lc(d.projects[p.projectId]?.name) === lc(c.name);
    case 'text':
      return c.value.split(/\s+/).every((w) => `${p.title} ${d.projects[p.projectId]?.name ?? ''}`.toLowerCase().includes(w));
    default:
      return true;
  }
}

/** A predicate for one entity's rows, for list pages that filter by `where=`. */
export function sessionPredicate(clauses: readonly Clause[], d: AskData): (s: Session) => boolean {
  if (!clauses.length) return () => true;
  const tests = clauses.map((c) => compileSessionClause(c, d));
  return (s) => tests.every((t) => t(s));
}
export function machinePredicate(clauses: readonly Clause[], d: AskData): (m: Machine) => boolean {
  const ctx = { latest: newestVersion(d.machines), liveByMachine: liveCount(d, 'machineId') };
  return (m) => clauses.every((c) => machineMatches(m, c, d, ctx));
}
export function auditPredicate(clauses: readonly Clause[], d: AskData): (e: AuditEntry) => boolean {
  return (e) => clauses.every((c) => auditMatches(e, c, d));
}
export function checkoutPredicate(clauses: readonly Clause[], d: AskData): (x: Checkout) => boolean {
  return (x) => clauses.every((c) => checkoutMatches(x, c, d));
}

function liveCount(d: AskData, key: 'machineId' | 'projectId'): Map<string, number> {
  const m = new Map<string, number>();
  for (const s of Object.values(d.sessions)) {
    if (s.status === 'ended') continue;
    const k = s[key];
    if (k) m.set(k, (m.get(k) ?? 0) + 1);
  }
  return m;
}

// ---------------------------------------------------------------------------
// Answers
// ---------------------------------------------------------------------------

export interface AskRow {
  id: string;
  title: string;
  subtitle: string;
  /** Route that opens this row. */
  href: string;
  /** Status for the row's dot (a StatusTone name). */
  tone?: 'working' | 'waiting' | 'idle' | 'ended' | 'passed' | 'failed' | 'queued' | 'offline';
}

export interface AskGroup {
  key: string;
  label: string;
  count: number;
  /** Summed figure for the group (tokens or USD), when there is one. */
  value?: number;
  valueKind?: 'usd' | 'tokens';
}

export interface AskResult {
  query: AskQuery;
  total: number;
  /** The first rows (already sorted and limited). */
  rows: AskRow[];
  groups: AskGroup[] | null;
  /** "12 sessions failed today". */
  summary: string;
  /** A short note on how a word was read ("Failed means a subagent or workflow failed"). */
  note: string | null;
  /** Where the answer opens as a list view. */
  view: { path: string; params: Record<string, string> };
  /** Sum of the answer's figure (USD for spend). */
  value?: number;
}

const ENTITY_NOUN: Record<AskEntity, [string, string]> = {
  sessions: ['session', 'sessions'],
  machines: ['machine', 'machines'],
  projects: ['project', 'projects'],
  audit: ['action', 'actions'],
  spend: ['session', 'sessions'],
  pipelines: ['pipeline', 'pipelines'],
  checkouts: ['checkout', 'checkouts'],
};

function noun(entity: AskEntity, n: number): string {
  const [one, many] = ENTITY_NOUN[entity];
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
}

export function formatUsd(n: number): string {
  if (n >= 1000) return `$${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  if (n >= 100) return `$${Math.round(n)}`;
  return `$${n.toFixed(2)}`;
}

function dayKey(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function sortRows<T>(rows: T[], field: SortField | undefined, dir: 'asc' | 'desc', get: (r: T, f: SortField) => number | string | null): T[] {
  if (!field) return rows;
  const s = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = get(a, field);
    const y = get(b, field);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return s * (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true }));
  });
}

const ROWS_SHOWN = 6;

/** Session sort keys, shared by the Sessions page `sort=` parameter. */
export const SESSION_SORTS: Partial<Record<SortField, (s: Session, d: AskData) => number | string>> = {
  activity: (s) => s.lastActivity,
  started: (s) => s.startedAt,
  tokens: (s) => s.tokens.input + s.tokens.output,
  cost: (s, d) => sessionCostOf(s, d),
  turns: (s) => s.turns,
  name: (s) => s.title.toLowerCase(),
};

/** Run a query. Group answers sum tokens (sessions) or USD (spend). */
export function evaluateAsk(q: AskQuery, d: AskData): AskResult {
  const where = whereText(q.clauses);
  const words = where ? ` ${where}` : '';
  const describe = (n: number) => `${noun(q.entity, n)}${words}`;
  let note: string | null = null;

  if (q.entity === 'sessions' || q.entity === 'spend') {
    const pred = sessionPredicate(q.clauses, d);
    let list = Object.values(d.sessions).filter(pred);
    if (q.clauses.some((c) => c.kind === 'status' && c.value === 'failed')) note = 'Failed means the session ended on an error, or a subagent or workflow in it failed.';
    const sortField = q.sort?.field ?? (q.entity === 'spend' ? 'cost' : 'activity');
    const getter = SESSION_SORTS[sortField] ?? SESSION_SORTS.activity!;
    list = sortRows(list, sortField, q.sort?.dir ?? 'desc', (s) => getter(s, d));
    const total = list.length;
    const spendMode = q.entity === 'spend';
    const sumCost = spendMode ? list.reduce((a, s) => a + sessionCostOf(s, d), 0) : undefined;
    if (spendMode) note = d.sessionCost ? 'Costs from the costs module; sessions active in the window.' : 'Estimated from each session\'s tokens at list prices; sessions active in the window.';
    let groups: AskGroup[] | null = null;
    if (q.groupBy) {
      const keyOf = (s: Session): [string, string] => {
        switch (q.groupBy) {
          case 'project':
            return [s.projectId ?? '', d.projects[s.projectId ?? '']?.name ?? 'No project'];
          case 'machine':
            return [s.machineId, d.machines[s.machineId]?.name ?? 'Unknown machine'];
          case 'status':
            return sessionFailed(s) ? ['failed', 'Failed'] : [s.status, s.status[0]!.toUpperCase() + s.status.slice(1)];
          case 'model':
            return [modelLabel(s.model), modelLabel(s.model)];
          case 'day':
            return [dayKey(s.lastActivity), dayKey(s.lastActivity)];
          default:
            return ['all', 'All'];
        }
      };
      const m = new Map<string, AskGroup>();
      for (const s of list) {
        const [k, label] = keyOf(s);
        const g = m.get(k) ?? { key: k, label, count: 0, value: 0, valueKind: spendMode ? 'usd' : 'tokens' };
        g.count++;
        g.value! += spendMode ? sessionCostOf(s, d) : s.tokens.input + s.tokens.output;
        m.set(k, g);
      }
      groups = [...m.values()].sort((a, b) => (q.groupBy === 'day' ? b.key.localeCompare(a.key) : (b.value ?? 0) - (a.value ?? 0) || b.count - a.count));
      if (q.limit) groups = groups.slice(0, q.limit);
    }
    const limited = q.limit ? list.slice(0, q.limit) : list;
    const rows: AskRow[] = limited.slice(0, ROWS_SHOWN).map((s) => ({
      id: s.id,
      title: s.title,
      subtitle: [d.projects[s.projectId ?? '']?.name, d.machines[s.machineId]?.name, spendMode ? formatUsd(sessionCostOf(s, d)) : null].filter(Boolean).join(', '),
      href: `/sessions?session=${encodeURIComponent(s.id)}`,
      tone: sessionFailed(s) && q.clauses.some((c) => c.kind === 'status' && c.value === 'failed') ? 'failed' : s.status,
    }));
    const params: Record<string, string> = {};
    if (where) params.where = where;
    if (q.groupBy === 'project' || q.groupBy === 'machine' || q.groupBy === 'status') params.group = q.groupBy;
    if (spendMode) params.group = q.groupBy === 'machine' ? 'machine' : 'project';
    if (q.sort || spendMode) params.sort = `${spendMode ? 'cost' : q.sort!.field}${(q.sort?.dir ?? 'desc') === 'asc' ? '.asc' : ''}`;
    const summary = spendMode
      ? `${formatUsd(sumCost ?? 0)} across ${noun('sessions', total)}${words}${q.groupBy ? ` by ${q.groupBy}` : ''}`
      : q.groupBy
        ? `${describe(total)} by ${q.groupBy}`
        : describe(q.limit ? Math.min(q.limit, total) : total);
    return { query: q, total, rows, groups, summary, note, view: { path: '/sessions', params }, value: sumCost };
  }

  if (q.entity === 'machines') {
    const pred = machinePredicate(q.clauses, d);
    let list = Object.values(d.machines).filter(pred);
    list = sortRows(list, q.sort?.field ?? 'name', q.sort?.dir ?? (q.sort ? 'desc' : 'asc'), (m, f) =>
      f === 'load' ? m.load : f === 'memory' ? 1 - m.memFree / (m.memTotal || 1) : f === 'activity' ? m.lastSeen : m.name.toLowerCase(),
    );
    const total = list.length;
    let groups: AskGroup[] | null = null;
    if (q.groupBy === 'os' || q.groupBy === 'status') {
      const m = new Map<string, AskGroup>();
      for (const x of list) {
        const k = q.groupBy === 'os' ? x.os : machineLiveness(x, d.now);
        const g = m.get(k) ?? { key: k, label: k[0]!.toUpperCase() + k.slice(1), count: 0 };
        g.count++;
        m.set(k, g);
      }
      groups = [...m.values()].sort((a, b) => b.count - a.count);
    }
    const shown = q.limit ? list.slice(0, q.limit) : list;
    const rows: AskRow[] = shown.slice(0, ROWS_SHOWN).map((m) => {
      const l = machineLiveness(m, d.now);
      return {
        id: m.id,
        title: m.name,
        subtitle: `${l === 'online' ? 'Online' : l === 'stale' ? 'Not reporting' : 'Offline'}, ${m.os}, load ${Math.round(m.load * 100)}%`,
        href: `/machines?machine=${encodeURIComponent(m.id)}`,
        tone: l === 'online' ? 'passed' : l === 'stale' ? 'waiting' : 'offline',
      };
    });
    if (q.clauses.some((c) => c.kind === 'status' && c.value === 'outdated')) note = `Outdated means an agent older than ${newestVersion(d.machines)}, the newest connected.`;
    return { query: q, total, rows, groups, summary: describe(total), note, view: { path: '/machines', params: where ? { where } : {} } };
  }

  if (q.entity === 'audit') {
    const pred = auditPredicate(q.clauses, d);
    let list = d.audit.filter(pred);
    if (q.sort) list = sortRows(list, q.sort.field, q.sort.dir, (e) => e.at);
    const total = list.length;
    let groups: AskGroup[] | null = null;
    if (q.groupBy) {
      const m = new Map<string, AskGroup>();
      for (const e of list) {
        const [k, label] =
          q.groupBy === 'actor'
            ? [e.actor, e.actor]
            : q.groupBy === 'machine'
              ? [e.machineId, d.machines[e.machineId]?.name ?? 'Removed machine']
              : q.groupBy === 'result' || q.groupBy === 'status'
                ? [e.result, e.result === 'ok' ? 'Succeeded' : 'Failed']
                : q.groupBy === 'day'
                  ? [dayKey(e.at), dayKey(e.at)]
                  : [e.action, ACTION_LABEL[e.action]];
        const g = m.get(k) ?? { key: k, label, count: 0 };
        g.count++;
        m.set(k, g);
      }
      groups = [...m.values()].sort((a, b) => b.count - a.count);
    }
    const shown = q.limit ? list.slice(0, q.limit) : list;
    const rows: AskRow[] = shown.slice(0, ROWS_SHOWN).map((e) => ({
      id: e.id,
      title: `${ACTION_LABEL[e.action]}: ${e.target}`,
      subtitle: `${e.actor}, ${d.machines[e.machineId]?.name ?? 'removed machine'}${e.result === 'error' ? ', failed' : ''}`,
      href: `/audit?entry=${encodeURIComponent(e.id)}`,
      tone: e.result === 'ok' ? 'passed' : 'failed',
    }));
    note = 'Covers the audit entries loaded in this tab.';
    return { query: q, total, rows, groups, summary: describe(total), note, view: { path: '/audit', params: where ? { where } : {} } };
  }

  if (q.entity === 'projects') {
    const live = liveCount(d, 'projectId');
    let list = Object.values(d.projects).filter((p) => q.clauses.every((c) => projectMatches(p, c, d, live)));
    list = sortRows(list, 'name', 'asc', (p) => p.name.toLowerCase());
    const rows: AskRow[] = list.slice(0, ROWS_SHOWN).map((p) => ({
      id: p.id,
      title: p.name,
      subtitle: `${live.get(p.id) ?? 0} live sessions`,
      href: `/projects?project=${encodeURIComponent(p.id)}`,
    }));
    return { query: q, total: list.length, rows, groups: null, summary: describe(list.length), note, view: { path: '/projects', params: {} } };
  }

  if (q.entity === 'checkouts') {
    const pred = checkoutPredicate(q.clauses, d);
    const list = Object.values(d.checkouts).filter(pred);
    const rows: AskRow[] = list.slice(0, ROWS_SHOWN).map((x) => ({
      id: x.id,
      title: `${d.projects[x.projectId]?.name ?? 'Unknown project'}: ${x.branch ?? x.head.slice(0, 7)}`,
      subtitle: `${d.machines[x.machineId]?.name ?? 'Unknown machine'}, ${x.dirty} changed, ${x.behind} behind`,
      href: `/projects?project=${encodeURIComponent(x.projectId)}`,
    }));
    return { query: q, total: list.length, rows, groups: null, summary: describe(list.length), note, view: { path: '/projects', params: where ? { where } : {} } };
  }

  // pipelines
  const list = Object.entries(d.pipelines).filter(([, p]) => q.clauses.every((c) => pipelineMatches(p, c, d)));
  const rows: AskRow[] = list.slice(0, ROWS_SHOWN).map(([key, p]) => ({
    id: key,
    title: `${d.projects[p.projectId]?.name ?? 'Unknown project'}: ${p.title}`,
    subtitle: `${p.state}, ${p.items.length} in flight, on ${d.machines[p.machineId]?.name ?? 'unknown machine'}`,
    href: `/pipelines?pipeline=${encodeURIComponent(key)}`,
    tone: p.state === 'running' ? 'working' : p.state === 'paused' ? 'waiting' : p.state === 'error' ? 'failed' : p.state === 'down' ? 'offline' : 'idle',
  }));
  return { query: q, total: list.length, rows, groups: null, summary: describe(list.length), note, view: { path: '/pipelines', params: {} } };
}

/** True when the text reads as a question worth answering (names an entity or a known clause). */
export function looksLikeAsk(q: AskQuery): boolean {
  return q.entityNamed || q.clauses.some((c) => c.kind !== 'text') || q.count || q.groupBy != null;
}

export type { GroupField };
